import { useEffect } from "react";
import { useAnalysisStore } from "./analysis.store";
import { analysisRunner } from "./analysis-runner";
import { useAnalysisDataStore } from "./analysis-data.store";
import type { UnanalyzedGame } from "./analysis.api";
import type { AnalysisPreset } from "./analysis-presets";
import { useAuth } from "@/app/context/AuthContext";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { resolveAnalysisAction } from "@/features/entitlements/entitlement-policy";

export interface UseAnalysisReturn {
  unanalyzed: UnanalyzedGame[];
  loading: boolean;
  startAnalysis: (presetOverride?: AnalysisPreset, games?: UnanalyzedGame[]) => void;
  stopAnalysis: () => void;
  refreshUnanalyzed: () => Promise<void>;
}

export function useAnalysis(): UseAnalysisReturn {
  const { userId } = useAuth();
  const preset = useAnalysisStore((state) => state.preset);
  const ownerUserId = useAnalysisDataStore((state) => state.ownerUserId);
  const ownedUnanalyzed = useAnalysisDataStore((state) => state.unanalyzed);
  const ownedLoading = useAnalysisDataStore((state) => state.isLoading);
  const entitlementSnapshot = useEntitlementsStore((state) => state.snapshot);
  const setOwner = useAnalysisDataStore((state) => state.setOwner);
  const loadUnanalyzed = useAnalysisDataStore((state) => state.loadUnanalyzed);
  const refreshUnanalyzed = useAnalysisDataStore((state) => state.refreshUnanalyzed);
  const isCurrentOwner = ownerUserId === userId;
  const available = isCurrentOwner ? (ownedUnanalyzed ?? []) : [];
  const eligible = available.filter(
    (game) => resolveAnalysisAction(entitlementSnapshot, game.id, false, game.exactTimeControl).allowed,
  );
  const unanalyzed =
    entitlementSnapshot && !entitlementSnapshot.membership.active && !entitlementSnapshot.onboarding.complete
      ? eligible.slice(0, 1)
      : eligible;
  const loading = !isCurrentOwner || ownedLoading;

  useEffect(() => {
    setOwner(userId);
    void loadUnanalyzed().catch(() => undefined);
  }, [loadUnanalyzed, setOwner, userId]);

  const startAnalysis = (presetOverride?: AnalysisPreset, games: UnanalyzedGame[] = unanalyzed) => {
    if (games.length === 0) return;
    // Not awaited — analysisRunner.start() keeps going independently of
    // this component; the store updates drive the UI as it progresses.
    void analysisRunner.start(games, presetOverride ?? preset);
  };

  const stopAnalysis = () => {
    analysisRunner.stop();
  };
  return { unanalyzed, loading, startAnalysis, stopAnalysis, refreshUnanalyzed };
}
